import React from 'react';
import { useQuery } from 'react-query';
import * as env from 'config/environment';
import * as consts from 'config/consts';
import { GetReportResult, getReport, getPositions } from 'config/api';
import { useRouting } from 'hooks/useRouting';
import { useModal } from 'hooks/useModal';
import { getViewedWebPushStorage } from 'utils/storage';
import { reportLoadingStyle } from 'pages/NewReport';
import { NoticeSettingModal } from 'components/features/modal/NoticeSettingModal';
import { getUserStorage } from 'components/features/utils/utils';
import { PositionItemsProps } from 'components/features/home/ProfitAndLossStuffing/ProfitAndLossStuffing';
import { isHiddenProductTypes } from 'components/features/home/PortfolioStuffing/asset/PortfolioStuffingAsset';
import Loading from 'components/commons/loading/Loading';
import { AssetList } from './components/AssetList';
import { DepositAndWithdrawalHistory } from './components/DepositAndWithdrawalHistory';
import { Notification } from './components/Notification';
import { AssetChange } from './components/AssetChange';

export const yyyymmddToDate = (val: string): Date | null => {
  const regex = /^(\d{4})(\d{2})(\d{2})$/;
  const result = regex.exec(val);
  if (result === null) return null;
  return new Date(Date.parse(`${result[1]}-${result[2]}-${result[3]}`));
};

export const APIFail: React.FC<Record<string, unknown>> = () => {
  return (
    <>
      <div className="bg-white">
        <div className="flex flex-col items-center justify-start py-8 px-6">
          <div
            className="mb-6 text-lg text-center font-bold text-bluegray-700"
            style={{ lineHeight: '18px' }}>
            準備中です
          </div>
          <img src="/static/images/report/report_stand-by.svg" alt="" />
          <div className="w-full mt-6 mb-10 mx-auto text-left text-xs text-black">
            日次レポートは毎日夜発行（土日除く）されます。
            <br />
            初回レポートが作成されるまでもうしばらくお待ち下さい。
          </div>
          <div className="w-full">
            <a id="dailyreport_line" href={env.lineAddFriendURL}>
              <div className="h-24 flex flex-row items-center px-3 border border-bluegray-300 bg-white rounded-lg cursor-pointer">
                <div className="w-16">
                  <img
                    src="/static/images/report/line_notice.svg"
                    alt=""
                    className="mx-auto"
                  />
                </div>
                <div className="flex flex-col ml-4 font-bold">
                  <div className="text-xs">毎日の資産変動をお知らせ</div>
                  <div className="text-base">LINEで通知を受け取る</div>
                </div>
                <div className="icon-external-link ml-auto mr-2 text-bluegray-500"></div>
              </div>
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export type OrderItemProps = {
  brokerID: string;
  brokerName: string;
  tradeTime: string | null;
  symbol: string;
  symbolName: string;
  accountType: number;
  productType: number;
  cashMargin: number;
  tradeType: number;
  side: number;
  qty: number | null;
  price: number | null;
  openPrice: number | null;
  openPriceUSD: number | null;
  profitOrLoss: number | null;
  profitOrLossUSD: number | null;
  currencyUnit: string | null;
};

export const DailyReport = ({ isLinkedLine }: { isLinkedLine: boolean }) => {
  const userStorage = getUserStorage();
  const viewedWebPushStorage = getViewedWebPushStorage();
  const userAgent = window.navigator.userAgent.toLowerCase();
  const isiPhone = /iphone|ipod/.test(userAgent);
  const isiPad = /ipad|macintosh/.test(userAgent) && 'ontouchend' in document;

  const initialValue =
    !isiPhone &&
    !isiPad &&
    userStorage !== null &&
    !userStorage.RegisteredWebPush &&
    (viewedWebPushStorage === null || !viewedWebPushStorage.dailyReport);

  const [
    isShowNoticeSettingModal,
    { hideModal: hideNoticeSettingModal },
  ] = useModal(initialValue);

  const { pushRouting: pushRouting_ } = useRouting();
  const pushRouting = (path: string): void =>
    pushRouting_({ path }, true, window.scrollY);

  const onAccountItemClick = (account: string): void => {
    pushRouting(`/accountdetail/${account}`);
  };
  const onTradeItemClick = (symbol: string): void => {
    pushRouting(`/tradedetail/${symbol}`);
  };

  const { data: reportData } = useQuery('dailyReport', getReportAPI, {
    refetchOnWindowFocus: false,
  });

  const { data: positionsData } = useQuery('dailyPositions', getPositionsAPI, {
    refetchOnWindowFocus: false,
  });

  if (reportData === null || reportData === undefined)
    return <Loading style={reportLoadingStyle} loading={true} />;

  if (reportData.Day === null) return <APIFail />;

  if (positionsData === null || positionsData === undefined) return <></>;

  const positions_ = (positionsData as PositionItemsProps[]) || [];
  // memo: その他、預金・MRF項目は非表示のため除外
  const positions = positions_.filter(
    position => !isHiddenProductTypes(position.productType)
  );

  const targetDate =
    reportData.Day !== undefined && reportData.Day.Base.From
      ? yyyymmddToDate(reportData.Day.Base.From.toString())
      : null;

  return (
    <>
      <div className="bg-white">
        <AssetChange
          {...{ report: reportData as GetReportResult, positions }}
        />
        <AssetList
          {...{
            positions,
            onTradeItemClick,
          }}
        />
      </div>
      <DepositAndWithdrawalHistory {...{ targetDate, onAccountItemClick }} />
      <Notification {...{ isLinkedLine }} />
      <NoticeSettingModal
        {...{
          isShow: isShowNoticeSettingModal,
          hide: hideNoticeSettingModal,
        }}
      />
    </>
  );
};

const getReportAPI = async () => {
  return await getReport(consts.reportType.日次);
};

const getPositionsAPI = async () => {
  const res_ = await getPositions(undefined, consts.reportType.日次);
  const res = res_.Items.map(res => {
    return {
      brokerID: res.BrokerID,
      account: res.Account,
      symbol: res.Symbol,
      symbolName: res.SymbolName,
      accountType: res.AccountType,
      productType: res.ProductType,
      cashMargin: res.CashMargin,
      side: res.Side,
      qty: res.Qty,
      price: res.Price,
      currentAmount: res.CurrentAmount,
      currentAmountUSD: res.CurrentAmountUSD,
      profitOrLoss: res.ProfitOrLoss,
      profitOrLossUSD: res.ProfitOrLossUSD,
      validationAmount: res.ValidationAmount,
      diffProfitOrLoss: res.DiffProfitOrLoss,
      diffProfitOrLossUSD: res.DiffProfitOrLossUSD,
      diffProfitOrLossRate: res.DiffProfitOrLossRate,
      currencyUnit: res.CurrencyUnit,
      status: res.Status,
    };
  });
  return res;
};
